import { Alpine } from "../../../vendor/livewire/livewire/dist/livewire.esm";

const QUESTION_TIME_LIMIT = 10;
const TIMER_INTERVAL_MS = 250; // ⚡ Reduced frequency: 250ms instead of 100ms

Alpine.data("point_quiz", (count, quizId) => ({
    started: false,
    finished: false,
    currentIndex: 0,
    count: count,
    hasAnswered: false,
    lastAnswerCorrect: false,
    timeLeft: QUESTION_TIME_LIMIT,
    points: QUESTION_TIME_LIMIT,
    totalScore: 0,
    quiz_avg: null,
    timerInterval: null,
    timerStart: null,
    quizId: quizId,
    supabaseUrl: null,
    supabaseAnonKey: null,
    
    init() {
        // ⚡ Deferred DOM queries to avoid blocking initialization
        requestAnimationFrame(() => {
            this.initSupabaseConfig();
        });
    },

    initSupabaseConfig() {
        try {
            this.supabaseUrl = document.querySelector('meta[name="supabase-url"]')?.getAttribute('content');
            this.supabaseAnonKey = document.querySelector('meta[name="supabase-anon-key"]')?.getAttribute('content');
            
            if (!this.supabaseUrl || !this.supabaseAnonKey) {
                console.warn("Supabase environment variables are not set.");
            }
        } catch(e) {
            console.warn("Failed to initialize Supabase config:", e);
        }
    },

    startQuiz() {
        this.started = true;
        // ⚡ Deferred timer start to avoid blocking UI
        requestAnimationFrame(() => {
            this.startTimer();
        });

        const questions = document.querySelectorAll('.question');
        for(let q = 0; q < questions.length; q++){
            
            const answers = Array.from(questions[q].querySelectorAll('.answer'));

            for (let i = answers.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [answers[i], answers[j]] = [answers[j], answers[i]];
            }

            const container = answers[0].parentNode;
            answers.forEach(l => container.appendChild(l));
        };
        
    },

    resetQuiz() {
        // ⚡ Safe interval clearing
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        
        this.finished = false;
        this.started = false;
        this.currentIndex = 0;
        this.hasAnswered = false;
        this.totalScore = 0;
        this.timeLeft = QUESTION_TIME_LIMIT;
        this.points = QUESTION_TIME_LIMIT;
        this.lastAnswerCorrect = false;
        this.quiz_avg = null;
        
    },

    fetchQuizResults() {
        if (!this.supabaseUrl || !this.supabaseAnonKey) {
            console.warn("Supabase environment variables are not set.");
            return Promise.resolve();
        }

        // ⚡ Timeout and error handling for mobile networks
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

        return fetch(`${this.supabaseUrl}/rest/v1/quiz_results?quiz_id=eq.${this.quizId}&select=score`, {
            headers: {
                "apikey": this.supabaseAnonKey,
                "Content-Type": "application/json"
            },
            signal: controller.signal
        })
        .then(response => {
            clearTimeout(timeoutId);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then(data => {
            if (data && data.length > 0) {
                const totalScore = data.reduce((sum, row) => sum + (row.score || 0), 0);
                this.quiz_avg = (totalScore / data.length).toFixed(1);
            } else {
                this.quiz_avg = null;
            }
        })
        .catch(error => {
            clearTimeout(timeoutId);
            if (error.name !== 'AbortError') {
                console.warn("Fetch error:", error);
            }
            this.quiz_avg = null;
        });
    },

    submitQuizResult(quizId, score) {
        if (!this.supabaseUrl || !this.supabaseAnonKey) {
            console.warn("Supabase environment variables are not set.");
            return Promise.resolve();
        }

        // ⚡ Timeout and error handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

        return fetch(`${this.supabaseUrl}/rest/v1/quiz_results`, {
            method: 'POST',
            headers: {
                "apikey": this.supabaseAnonKey,
                "Content-Type": "application/json",
                "Prefer": "return=minimal"
            },
            body: JSON.stringify([{
                quiz_id: quizId,
                score: Number(score.toFixed(1)),
                max_score: Number((this.count * 10).toFixed(1))
            }]),
            signal: controller.signal
        })
        .then(response => {
            clearTimeout(timeoutId);
            if (!response.ok) {
                throw new Error(`Error submitting score: ${response.status}`);
            }
            return this.fetchQuizResults();
        })
        .catch(error => {
            clearTimeout(timeoutId);
            if (error.name !== 'AbortError') {
                console.warn("Submit error:", error);
            }
        });
    },

    checkAnswer(isCorrect) {
        if (this.hasAnswered) return;

        // ⚡ Safe timer cleanup
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        
        this.updateTimerPoints();
        this.hasAnswered = true;
        this.lastAnswerCorrect = isCorrect;

        if (isCorrect) {
            this.totalScore += this.points;
        } else {
            this.points = 0;
        }
    },

    next() {
        if (!this.hasAnswered) {
            // ⚡ Non-blocking alert alternative
            console.warn("User must answer before continuing");
            return;
        }

        this.hasAnswered = false;
        this.lastAnswerCorrect = false;
        this.currentIndex++;

        // ⚡ Simplified transition handling
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.startTimer();
            });
        });
    },

    showResults() {
        // ⚡ Safe timer cleanup
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        
        this.finished = true;
        this.started = false;
        
        // ⚡ Non-blocking result submission
        requestAnimationFrame(() => {
            this.submitQuizResult(this.quizId, this.totalScore);
        });
    },

    startTimer() {
        // ⚡ Safe cleanup before starting new timer
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        
        this.timeLeft = QUESTION_TIME_LIMIT;
        this.points = QUESTION_TIME_LIMIT;
        this.timerStart = Date.now();

        // ⚡ Reduced frequency timer for better mobile performance
        this.timerInterval = setInterval(() => {
            this.updateTimerPoints();
            if (this.timeLeft <= 0) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
                this.hasAnswered = true;
                this.points = 0;
            }
        }, TIMER_INTERVAL_MS); // Now 250ms instead of 100ms
    },

    updateTimerPoints() {
        if (!this.timerStart) return;
        
        const elapsed = (Date.now() - this.timerStart) / 1000;
        this.timeLeft = Math.max(QUESTION_TIME_LIMIT - elapsed, 0);
        this.points = Math.max(parseFloat(this.timeLeft.toFixed(1)), 0);
    },

    retryQuiz() {
        this.resetQuiz();
        this.started = false;
        // ⚡ Deferred timer start
        requestAnimationFrame(() => {
            this.startTimer();
        });
    },

}));